import { useCallback, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';

import { Modal } from '@/core/components/Modal/Modal';
import { useAppDispatch } from '@/core/store/store';
import { Paragraph, Heading } from '@/core/components/Typography';
import { Icon } from '@/core/components/Icons';
import { useDecodedCategories } from '@/core/hooks/useDecodedCategories';
import {
  REPORT_PUBLISHED_FORMAT,
  EVENT_DATE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@/core/constants/time';
import { getSourceIcon } from '@/core/constants/reports';
import { HEADER_HEIGHT } from '@/core/components/Layout/Layout.styles';
import { ReportReadStatus, ReportSourceType, UserRole } from '@/core/interfaces/common';
import { useReportReadStatus } from '@/core/hooks/useReportReadStatus';
import { useTopmostZIndex } from '@/core/hooks/useTopmostZIndex';

import { closeDetailsModal, markReportAsOpened } from '@/features/Reports/store/slices';
import { getActiveReportDataSelector } from '@/features/Reports/store';
import { getReportCategoriesNames, getReportCountriesNames } from '@/features/Reports/helpers';
import { getUserSelector } from '@/features/Auth/store';

import { getTimeAndDate, hasPermission, isG4S } from '@/utils/helpers';

import { ReportSections } from './components/ReportSections';
import {
  DetailsContainer,
  DetailsHeader,
  ReportTitle,
  Country,
  HeaderTitleRow,
  HeaderIcon,
  HeaderIconsContainer,
  HeaderSourceColumn,
  HeaderSourceIcon,
} from './ReportDetails.styles';
import { ReportDetailsProps } from './ReportDetails.types';

export const ReportDetails = ({ isOpen }: ReportDetailsProps) => {
  const zIndex = useTopmostZIndex(isOpen);
  const report = useSelector(getActiveReportDataSelector);
  const { colors } = useTheme();
  const dispatch = useAppDispatch();
  const userData = useSelector(getUserSelector);

  const { markReportInBrowser } = useReportReadStatus();

  const categories = useDecodedCategories(getReportCategoriesNames(report?.category || []));

  const closeModal = useCallback(() => {
    dispatch(closeDetailsModal());
  }, [dispatch]);

  const dateFormat = useMemo(() => {
    const eventStartDate = report?.eventDate
      ? getTimeAndDate({
          dateStr: report.eventDate,
          enterFormat: DEFAULT_DATE_FORMAT,
          exitFormat: EVENT_DATE_FORMAT,
        })
      : null;
    const eventEndDate = report?.eventEndDate
      ? getTimeAndDate({
          dateStr: report.eventEndDate,
          enterFormat: DEFAULT_DATE_FORMAT,
          exitFormat: EVENT_DATE_FORMAT,
        })
      : null;
    const eventStartLocalTime = report?.eventTime;
    const eventEndLocalTime = report?.eventEndTime;

    const publishDate =
      report?.reportSourceType === ReportSourceType.LOOKOUT
        ? report.modifiedDatetime
        : report?.publishedDatetime;

    return {
      timePublished: publishDate
        ? getTimeAndDate({
            dateStr: publishDate,
            exitFormat: REPORT_PUBLISHED_FORMAT,
          })
        : null,
      eventDate:
        eventStartDate && eventEndDate ? `${eventStartDate} - ${eventEndDate}` : eventStartDate,
      eventLocalTime:
        eventStartLocalTime && eventEndLocalTime
          ? `${eventStartLocalTime} - ${eventEndLocalTime}`
          : eventStartLocalTime,
    };
  }, [report]);

  const reportCountry = useMemo(() => getReportCountriesNames(report?.country || []), [report]);

  const reportLocationText = useMemo(
    () => report?.location.map(reportLocation => reportLocation.address).join(' - ') || '',
    [report?.location]
  );

  const canEditReport = useMemo(
    () =>
      !isG4S &&
      hasPermission(userData?.groups || [], [
        UserRole.MANAGEMENT_ADMIN,
        UserRole.MANAGEMENT_EDITOR,
        UserRole.MANAGEMENT_ANALYST,
        UserRole.MANAGEMENT_JUNIOR_ANALYST,
      ]),
    [userData?.groups]
  );

  const editUrl = useMemo(() => {
    if (report?.reportSourceType === ReportSourceType.LOOKOUT) {
      return `https://admin.hozint.ai/report/lookout/${report.reportId}/update/`;
    }

    if (report?.reportSourceType === ReportSourceType.HORIZON) {
      return `https://admin.hozint.ai/report/horizon/${report.reportId}/update/`;
    }

    return '';
  }, [report]);

  useEffect(() => {
    if (report) {
      dispatch(
        markReportAsOpened({
          reportId: report.reportId,
          reportSourceType: report.reportSourceType,
        })
      );

      markReportInBrowser(report, ReportReadStatus.READ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => closeModal()}
      zIndex={zIndex}
      height={`calc(100vh - ${HEADER_HEIGHT})`}
      matchContentSize
      overlayPosition={{
        top: HEADER_HEIGHT,
        right: '0',
        left: 'auto',
      }}
      overlayClassName="slide-from-right"
      closeTimeoutMS={300}
    >
      {report && (
        <DetailsContainer>
          <DetailsHeader>
            <HeaderTitleRow>
              <ReportTitle>
                <Heading type="h2">
                  <Country>{reportCountry} | </Country>
                  {report.reportTitle}
                </Heading>
              </ReportTitle>
              <HeaderIconsContainer>
                {canEditReport && (
                  <HeaderIcon
                    as="a"
                    href={editUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="PenToSquare" />
                  </HeaderIcon>
                )}
                <HeaderSourceColumn>
                  <HeaderIcon onClick={closeModal}>
                    <Icon name="Close" />
                  </HeaderIcon>
                  <HeaderSourceIcon>
                    {getSourceIcon(report.reportSourceType, colors.active, 48)}
                  </HeaderSourceIcon>
                </HeaderSourceColumn>
              </HeaderIconsContainer>
            </HeaderTitleRow>
            <Paragraph
              type="normal"
              color={colors.secondaryText}
            >
              {`Published on ${dateFormat.timePublished}`}
            </Paragraph>
          </DetailsHeader>
          <ReportSections
            report={report}
            country={reportCountry}
            locationAddress={reportLocationText}
            eventDate={dateFormat.eventDate}
            eventLocalTime={dateFormat.eventLocalTime}
            categories={categories}
          />
        </DetailsContainer>
      )}
    </Modal>
  );
};
